import { actionDescriptionLocoKeys, actionTitleLocoKeys } from 'modules/automation-rules/actions/constants/action-loco-keys';
import { AutomationRuleActionsEnum } from 'modules/automation-rules/actions/enums/action-enum';
import { ActionsDataInterface, useActionsData } from 'modules/automation-rules/actions/hooks/use-actions-data';
import { getActionDataFromSlug } from 'modules/automation-rules/actions/utils/get-action-data-from-slug';
import { getActionIcon } from 'modules/automation-rules/actions/utils/get-action-icon';
import { RuleStepHeader } from 'modules/automation-rules/components/rule-step-header';
import React, { type JSX } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';

/**
 * Combobox.InputLabel's own classes, replicated for a label row rendered outside the native
 * <label> - a <label> may not contain other labelable controls, so fields with inline
 * create/edit buttons build this row themselves instead of passing it through FormSelect's
 * label prop. The required asterisk is scoped to the text span so it sits right after the
 * label text, not after the trailing icon buttons.
 */
export const ACCESSIBLE_LABEL_ROW_CLASSNAME = 'text-text-secondary flex items-center gap-1.5 text-base font-normal';
export const ACCESSIBLE_LABEL_REQUIRED_CLASSNAME = "data-required:after:text-text-error data-required:after:ml-0.5 data-required:after:content-['*']";
export interface WrapperChildrenPropsInterface {
  renderActionLabel: ({
    label,
    additionalLabel
  }: {
    label: string;
    additionalLabel?: JSX.Element;
  }) => JSX.Element;
  onOpen: () => void;
  data: ActionsDataInterface;
}
type ChildrenType = (args: WrapperChildrenPropsInterface) => JSX.Element;
interface EditActionWrapperProps {
  slug: AutomationRuleActionsEnum;
  children: ChildrenType;
  onDelete: () => void;
}
const EditActionWrapper = ({
  slug,
  children,
  onDelete
}: EditActionWrapperProps) => {
  const {
    t
  } = useLocoTranslation();
  const {
    data,
    setShouldFetch
  } = useActionsData();
  const renderActionLabel = ({
    label,
    additionalLabel
  }: {
    label: string;
    additionalLabel?: JSX.Element;
  }) => <>
      <span>{label}</span>
      {additionalLabel ? <span className={'order-1 ml-1.5 flex h-5 items-center gap-1.5 self-center [&_svg]:size-4 [&_svg]:shrink-0'}>
          {additionalLabel}
        </span> : null}
    </>;
  const childrenData = {
    renderActionLabel,
    onOpen: () => setShouldFetch(prev => ({
      ...prev,
      [getActionDataFromSlug(slug)]: true
    })),
    data
  };
  return <div className={'flex flex-col gap-4'}>
      <RuleStepHeader description={t(actionDescriptionLocoKeys[slug])} icon={getActionIcon(slug)} onDelete={onDelete} title={t(actionTitleLocoKeys[slug])} />
      {children(childrenData)}
    </div>;
};
export default EditActionWrapper;